import React, { useContext, useState } from 'react';
import { useQuery } from 'react-apollo';
import { FlowContext } from '../../core/flow-engine';
import { CartContextProvider } from '../../context/CartContext/CartContext';
import { ALL_FUNDS_QUERY } from '../../components/FundListFragment/queries';
import FONASUCustomerInformation from '../../components/FONASUCustomerInformation/FONASUCustomerInformation';
import RPSOCRBeneficiaryInfo from '../../components/RPSOCRBeneficiaryInfo/RPSOCRBeneficiaryInfo';
import RPSOCRChooseMyFunds from '../../components/RPSOCRChooseMyFunds/RPSOCRChooseMyFunds';
import FONASUHelpText from '../../components/FONASUHelpText/FONASUHelpText';
import RPSOCRPaperlessPreferences from '../../components/RPSOCRPaperlessPreferences/RPSOCRPaperlessPreferences';
import RPSOCRDeliveryMethod from '../../components/RPSOCRDeliveryMethod/RPSOCRDeliveryMethod';
import RPSOCRConsentAndAgreement from '../../components/RPSOCRConsentAndAgreement/RPSOCRConsentAndAgreement';
import RPSTRACDistribution from '../../components/RPSTRACDistribution/RPSTRACDistribution';
import RPSTRACRolloverOptions from '../../components/RPSTRACRolloverOptions/RPSTRACRolloverOptions';
import RPSOCRExistingFunds from '../../components/RPSOCRExistingFunds/RPSOCRExistingFunds';
import RetirementIncomeTaxWitholding from '../../components/RetirementIncomeTaxWithholding/RetirementIncomeTaxWithholding';
import { formatFunds } from '../../flows/shared/utils';
import { getOCRPageTitle } from '../../flows/front-office/ocr-flows/utils';
import styles from './RPSOneCallRolloverScreen.css';

import Heading from '../../components/Typography/Heading/Heading';
import { MFBeaconButton } from '../../components/Beacon/BeaconComponents';
import AlertMessage from '../../components/AlertMessage/AlertMessage';

import {
  isOCRSubmitAllowed,
  checkValid,
  doneMode,
  checkSubmitValid,
  allSectionsInDoneMode,
  isTRACDistributionValid,
  displayDistributionSection
} from './utils';

/* eslint-disable complexity */
// ***********************************************************************
// TODO [2021-08-25] @Jay: Move of some the logic into its own component
// ***********************************************************************

const RPSOneCallRolloverScreenContents = ({
  children,
  editCustomer,
  editExistingAgreements,
  setEditExistingAgreements,
  setEditCustomer,
  editBeneInfo,
  setEditBeneInfo,
  investmentAmt,
  setInvestmentAmt,
  editPortfolio,
  setEditPortfolio,
  editPaperless,
  setEditPaperless,
  editDeliveryMethod,
  setEditDeliveryMethod,
  consentAgreement,
  setConsentAgreement,
  showPrimaryBeneRelationshipError,
  setShowPrimaryBeneRelationshipError,
  showPrimaryBeneDistributionError,
  setShowPrimaryBeneDistributionError,
  showSecondaryBeneRelationshipError,
  setShowSecondaryBeneRelationshipError,
  showSecondaryBeneDistributionError,
  setShowSecondaryBeneDistributionError,
  paperlessWithdrawls,
  setPaperlessWithdrawls,
  participantStatus,
  setParticipantStatus,
  participantAge,
  setParticipantAge,
  brokerageAssets,
  setBrokerageAssets,
  participantLoanBalance,
  setParticipantLoanBalance,
  participantCashDistribution,
  setParticipantCashDistribution,
  distributionStatus,
  setDistributionStatus,
  setRolloverOptionsNotice,
  next,
  alert,
  error,
  paperlessConsentValid,
  paperlessDelivery,
  deliveryMethodValid,
  isOCRTracApp,
  existingAgreements,
  existingAgreementsValid,
  updatedExistingAgreements,
  existingAgreementType,
  accountType,
  userHaveExistingAgreements,
  hasRetirementIncomeFund
}) => {
  const { data: { allMutualFunds = [] } = {} } = useQuery(ALL_FUNDS_QUERY);

  // filter out suppressed fund
  const MutualFunds = allMutualFunds.filter(
    ({ openToNewInvestors, openToSubsequentInvestments }) =>
      openToNewInvestors || (!openToNewInvestors && openToSubsequentInvestments)
  );

  const pageTitle = getOCRPageTitle(accountType);

  const [selectedExistingFunds, setSelectedExistingFunds] = useState(
    updatedExistingAgreements
  );

  return (
    <>
      {alert && (
        <AlertMessage message="Account creation failed and the information was not submitted. Please try again." />
      )}
      {error !== '' && <AlertMessage message={error} />}
      <div className="wrapper">
        <Heading text={pageTitle} />
        <FONASUHelpText />

        <div className="review-section">
          <FONASUCustomerInformation
            editCustomer={editCustomer}
            setEditCustomer={setEditCustomer}
            showInvestmentAmt={false}
          />
        </div>

        {existingAgreements?.length > 0 && (
          <div className="review-section">
            <RPSOCRExistingFunds
              existingAgreements={existingAgreements}
              next={next}
              selectedExistingFunds={selectedExistingFunds}
              setSelectedExistingFunds={setSelectedExistingFunds}
              editExistingAgreements={editExistingAgreements}
              setEditExistingAgreements={setEditExistingAgreements}
              existingAgreementsValid={existingAgreementsValid}
              updatedExistingAgreements={updatedExistingAgreements}
              existingAgreementType={existingAgreementType}
              accountType={accountType}
            />
          </div>
        )}

        <div className="review-section">
          <RPSOCRChooseMyFunds
            editPortfolio={editPortfolio}
            setEditPortfolio={setEditPortfolio}
            investmentAmt={investmentAmt}
            setInvestmentAmt={setInvestmentAmt}
            allMutualFunds={MutualFunds}
            next={next}
            error={error}
          />
        </div>

        <div className="review-section">
          <RPSOCRBeneficiaryInfo
            editBeneInfo={editBeneInfo}
            setEditBeneInfo={setEditBeneInfo}
            showPrimaryBeneRelationshipError={showPrimaryBeneRelationshipError}
            setShowPrimaryBeneRelationshipError={
              setShowPrimaryBeneRelationshipError
            }
            showPrimaryBeneDistributionError={showPrimaryBeneDistributionError}
            setShowPrimaryBeneDistributionError={
              setShowPrimaryBeneDistributionError
            }
            showSecondaryBeneRelationshipError={
              showSecondaryBeneRelationshipError
            }
            setShowSecondaryBeneRelationshipError={
              setShowSecondaryBeneRelationshipError
            }
            showSecondaryBeneDistributionError={
              showSecondaryBeneDistributionError
            }
            setShowSecondaryBeneDistributionError={
              setShowSecondaryBeneDistributionError
            }
          />
        </div>

        <div className="review-section">
          <RPSOCRPaperlessPreferences
            next={next}
            editPaperless={editPaperless}
            setEditPaperless={setEditPaperless}
            paperlessConsentValid={paperlessConsentValid}
            paperlessDelivery={paperlessDelivery}
          />
        </div>

        {!userHaveExistingAgreements && (
          <div className="review-section">
            <RPSOCRDeliveryMethod
              editDeliveryMethod={editDeliveryMethod}
              setEditDeliveryMethod={setEditDeliveryMethod}
            />
          </div>
        )}

        {hasRetirementIncomeFund && (
          <div className="review-section">
            <RetirementIncomeTaxWitholding />
          </div>
        )}

        {!userHaveExistingAgreements && deliveryMethodValid && (
          <div className="review-section">
            <RPSOCRConsentAndAgreement
              consentAgreement={consentAgreement}
              setConsentAgreement={setConsentAgreement}
              accountType={accountType}
            />
          </div>
        )}

        {displayDistributionSection(
          isOCRTracApp,
          consentAgreement,
          userHaveExistingAgreements
        ) && (
          <>
            <div className="review-section">
              <RPSTRACDistribution
                setPaperlessWithdrawls={setPaperlessWithdrawls}
                paperlessWithdrawls={paperlessWithdrawls}
                participantStatus={participantStatus}
                setParticipantStatus={setParticipantStatus}
                participantAge={participantAge}
                setParticipantAge={setParticipantAge}
                brokerageAssets={brokerageAssets}
                setBrokerageAssets={setBrokerageAssets}
                participantLoanBalance={participantLoanBalance}
                setParticipantLoanBalance={setParticipantLoanBalance}
                participantCashDistribution={participantCashDistribution}
                setParticipantCashDistribution={setParticipantCashDistribution}
                distributionStatus={distributionStatus}
                setDistributionStatus={setDistributionStatus}
              />
            </div>
            <div className="review-section">
              <RPSTRACRolloverOptions
                setRolloverOptionsNotice={setRolloverOptionsNotice}
              />
            </div>
          </>
        )}

        <div className="btn-container">{children}</div>

        <style jsx>{styles}</style>
      </div>
    </>
  );
};

/* eslint-enable complexity */

const RPSOneCallRolloverScreen = ({ appState }) => {
  const { next, alert, flowProps } = useContext(FlowContext);
  const {
    store: {
      amount,
      accountType,
      loadedData: {
        selectedFunds,
        infoValid,
        portfolioValid,
        beneInfoValid,
        error,
        customerInfoEditMode,
        existingAgreementsEditMode,
        beneInfoEditMode,
        portfolioEditMode,
        paperlessEditMode,
        deliveryEditMode,
        paperlessConsentValid,
        paperlessDelivery,
        deliveryMethodValid,
        existingAgreements,
        existingAgreementsValid,
        updatedExistingAgreements,
        userHaveExistingAgreements,
        existingAgreementType,
        hasRetirementIncomeFund
      }
    }
  } = appState;

  const { isOCRTracApp } = flowProps;

  const [editCustomer, setEditCustomer] = useState(customerInfoEditMode);
  const [editPortfolio, setEditPortfolio] = useState(portfolioEditMode);
  const [editExistingAgreements, setEditExistingAgreements] = useState(
    existingAgreementsEditMode
  );
  const [editBeneInfo, setEditBeneInfo] = useState(beneInfoEditMode);
  const [investmentAmt, setInvestmentAmt] = useState(amount);

  const [editDeliveryMethod, setEditDeliveryMethod] = useState(
    deliveryEditMode
  );
  const [editPaperless, setEditPaperless] = useState(paperlessEditMode);
  const [consentAgreement, setConsentAgreement] = useState(false);
  const [
    showPrimaryBeneRelationshipError,
    setShowPrimaryBeneRelationshipError
  ] = useState(false);

  const [
    showPrimaryBeneDistributionError,
    setShowPrimaryBeneDistributionError
  ] = useState(false);

  const [
    showSecondaryBeneRelationshipError,
    setShowSecondaryBeneRelationshipError
  ] = useState(false);

  const [
    showSecondaryBeneDistributionError,
    setShowSecondaryBeneDistributionError
  ] = useState(false);

  // set initial state for TRAC distribution options
  const [paperlessWithdrawls, setPaperlessWithdrawls] = useState('');
  const [participantStatus, setParticipantStatus] = useState('');
  const [participantAge, setParticipantAge] = useState('');
  const [brokerageAssets, setBrokerageAssets] = useState('');
  const [participantLoanBalance, setParticipantLoanBalance] = useState('');
  const [
    participantCashDistribution,
    setParticipantCashDistribution
  ] = useState('');
  const [distributionStatus, setDistributionStatus] = useState('');
  const [rolloverOptionsNotice, setRolloverOptionsNotice] = useState('');

  const isValid =
    checkValid(infoValid, portfolioValid, beneInfoValid) &&
    doneMode(editCustomer, editBeneInfo, editPortfolio);

  const isSubmitValid =
    checkSubmitValid(
      infoValid,
      portfolioValid,
      beneInfoValid,
      paperlessConsentValid,
      deliveryMethodValid,
      userHaveExistingAgreements
    ) &&
    allSectionsInDoneMode(
      editCustomer,
      editBeneInfo,
      editPortfolio,
      editPaperless,
      editDeliveryMethod,
      userHaveExistingAgreements
    );

  const isTRACSubmitValid =
    isSubmitValid &&
    isTRACDistributionValid(
      paperlessWithdrawls,
      participantStatus,
      participantAge,
      brokerageAssets,
      participantLoanBalance,
      participantCashDistribution,
      distributionStatus,
      rolloverOptionsNotice
    );

  const disableSubmit = isOCRSubmitAllowed(
    isSubmitValid,
    isOCRTracApp,
    isTRACSubmitValid,
    consentAgreement,
    userHaveExistingAgreements
  );

  const formattedSelectedFunds = formatFunds(selectedFunds);

  const handleClick = async () => {
    if (isValid) {
      next('ocrSubmit')();
    }
  };

  return (
    <CartContextProvider
      initialTotalAmount={investmentAmt}
      initialPercentMode
      initialFunds={formattedSelectedFunds}>
      <RPSOneCallRolloverScreenContents
        editCustomer={editCustomer}
        setEditCustomer={setEditCustomer}
        editExistingAgreements={editExistingAgreements}
        setEditExistingAgreements={setEditExistingAgreements}
        editPortfolio={editPortfolio}
        setEditPortfolio={setEditPortfolio}
        editBeneInfo={editBeneInfo}
        setEditBeneInfo={setEditBeneInfo}
        editPaperless={editPaperless}
        setEditPaperless={setEditPaperless}
        investmentAmt={investmentAmt}
        setInvestmentAmt={setInvestmentAmt}
        editDeliveryMethod={editDeliveryMethod}
        setEditDeliveryMethod={setEditDeliveryMethod}
        consentAgreement={consentAgreement}
        setConsentAgreement={setConsentAgreement}
        paperlessWithdrawls={paperlessWithdrawls}
        setPaperlessWithdrawls={setPaperlessWithdrawls}
        participantStatus={participantStatus}
        setParticipantStatus={setParticipantStatus}
        participantAge={participantAge}
        setParticipantAge={setParticipantAge}
        brokerageAssets={brokerageAssets}
        setBrokerageAssets={setBrokerageAssets}
        participantLoanBalance={participantLoanBalance}
        setParticipantLoanBalance={setParticipantLoanBalance}
        participantCashDistribution={participantCashDistribution}
        setParticipantCashDistribution={setParticipantCashDistribution}
        distributionStatus={distributionStatus}
        setDistributionStatus={setDistributionStatus}
        setRolloverOptionsNotice={setRolloverOptionsNotice}
        showPrimaryBeneRelationshipError={showPrimaryBeneRelationshipError}
        setShowPrimaryBeneRelationshipError={
          setShowPrimaryBeneRelationshipError
        }
        showPrimaryBeneDistributionError={showPrimaryBeneDistributionError}
        setShowPrimaryBeneDistributionError={
          setShowPrimaryBeneDistributionError
        }
        showSecondaryBeneRelationshipError={showSecondaryBeneRelationshipError}
        setShowSecondaryBeneRelationshipError={
          setShowSecondaryBeneRelationshipError
        }
        showSecondaryBeneDistributionError={showSecondaryBeneDistributionError}
        setShowSecondaryBeneDistributionError={
          setShowSecondaryBeneDistributionError
        }
        next={next}
        alert={alert}
        error={error}
        paperlessConsentValid={paperlessConsentValid}
        paperlessDelivery={paperlessDelivery}
        deliveryMethodValid={deliveryMethodValid}
        isOCRTracApp={isOCRTracApp}
        existingAgreements={existingAgreements}
        existingAgreementsValid={existingAgreementsValid}
        updatedExistingAgreements={updatedExistingAgreements}
        accountType={accountType}
        userHaveExistingAgreements={userHaveExistingAgreements}
        existingAgreementType={existingAgreementType}
        hasRetirementIncomeFund={hasRetirementIncomeFund}>
        <MFBeaconButton
          id="ocrSubmitBtn"
          data-name="ocrSubmitBtn"
          disabled={!disableSubmit}
          className="beacon-button-margin-override"
          onClick={handleClick}
          variant="primary"
          label="Submit"
        />
      </RPSOneCallRolloverScreenContents>
    </CartContextProvider>
  );
};

export default RPSOneCallRolloverScreen;
